import { Slot } from "@radix-ui/react-slot";
import { type VariantProps, cva } from "class-variance-authority";
import { type ButtonHTMLAttributes, type ReactNode, forwardRef } from "react";
import { HStack } from "./layout/stack";
import { cn } from "./lib/utils";
import { Spinner } from "./loaders";

// 				// remove??
// 				primary: "bg-button-bg-brand text-surface",
// 				secondary: "bg-surface-highlight text-text-body-inverted",
// 				plain:
// 					"bg-surface border border-prompt-border-muted text-text-body-muted",
// 				// remove???
// 				default:
// 					"bg-button-bg-base text-button-text-base disabled:text-button-text-disabled hover:bg-button-bg-base-hover",
// 				ghost: "hover:bg-[rgba(0,0,0,0.08)] text-text-body",
// 				// remove??
// 				hidden: "hover:bg-[rgba(0,0,0,0.08)] text-text-body-muted",
// 				icon: "rounded-full text-text-body-icon hover:text-text-body hover:bg-[rgba(0,0,0,0.08)]",

const buttonVariants = cva(
	"inline-flex items-center justify-center gap-2 border border-[rgba(255,255,255,0.02)] whitespace-nowrap rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 disabled:cursor-not-allowed [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 transition-all duration-200 active:scale-[.99]",
	{
		variants: {
			intent: {
				default:
					"bg-button-bg-base text-button-text-base shadow hover:opacity-80",
				destructive: "bg-red-700 text-white shadow-sm hover:bg-red-500",
				outline:
					"border-button-border-base shadow-sm hover:bg-button-border-base text-text-body",
				brand:
					"bg-jace text-text-body-inverted shadow-sm hover:opacity-80 border-0",
				secondary:
					// TODO: add surface-highlight-hover color
					"bg-surface-highlight text-text-body-inverted shadow-sm hover:bg-surface-highlight-hover",
				black: "bg-black text-white shadow-sm hover:bg-black/70",
				ghost:
					"border-transparent hover:bg-button-bg-ghost hover:border-[rgba(255,255,255,0.02)]",
				link: "text-text-body underline-offset-4 hover:underline border-0",
			},
			size: {
				default: "h-8 px-4 text-sm",
				sm: "h-7 px-3 text-sm [&_svg]:size-3",
				lg: "h-10 rounded-lg px-6 text-md",
				icon: "h-8 w-8",
			},
		},
		defaultVariants: {
			intent: "default",
			size: "default",
		},
	},
);

interface ButtonProps
	extends ButtonHTMLAttributes<HTMLButtonElement>,
		VariantProps<typeof buttonVariants> {
	asChild?: boolean;
	isLoading?: boolean;
}

const Button = forwardRef<HTMLButtonElement, ButtonProps>(
	(
		{
			className,
			intent,
			size,
			asChild = false,
			isLoading = false,
			children,
			...props
		},
		ref,
	) => {
		const Comp = asChild ? Slot : "button";
		return (
			<Comp
				className={cn(buttonVariants({ intent, size, className }))}
				disabled={isLoading}
				ref={ref}
				{...props}
			>
				{isLoading ? (
					<span className="relative flex w-full items-center justify-center">
						<Spinner color="#fff" />
					</span>
				) : (
					children
				)}
			</Comp>
		);
	},
);
Button.displayName = "Button";

interface ButtonGroupProps {
	children: ReactNode;
	className?: string;
	asChild?: boolean;
}

const ButtonGroup = forwardRef<HTMLDivElement, ButtonGroupProps>(
	({ className, children, asChild }, ref) => {
		return (
			<HStack
				asChild={asChild}
				className={cn("flex-shrink-0 items-center gap-min", className)}
				ref={ref}
			>
				{children}
			</HStack>
		);
	},
);
ButtonGroup.displayName = "ButtonGroup";

export {
	Button,
	ButtonGroup,
	buttonVariants,
	type ButtonGroupProps,
	type ButtonProps,
};
